// cnTranslationService.ts
import i18n from '../i18n';

interface CNTranslations {
  [cnNumber: string]: string;
}

class CNTranslationService {
  private translations: Map<string, CNTranslations> = new Map();
  private loadingPromises: Map<string, Promise<CNTranslations>> = new Map();
  private translationKeys: Map<string, string[]> = new Map();

  private normalizeCode(cnNumber: string): string | null {
    if (cnNumber === null || cnNumber === undefined) {
      return null;
    }

    const normalized = `${cnNumber}`.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private getSortedKeys(locale: string, translations: CNTranslations): string[] {
    if (!this.translationKeys.has(locale)) {
      const keys = Object.keys(translations).sort(
        (a, b) => a.length - b.length || a.localeCompare(b),
      );
      this.translationKeys.set(locale, keys);
    }

    return this.translationKeys.get(locale)!;
  }

  private registerCachedTranslation(locale: string, code: string, value: string): void {
    const translations = this.translations.get(locale);
    if (!translations) {
      return;
    }

    translations[code] = value;
    this.translationKeys.delete(locale);
  }

  private findFallbackTranslation(
    cnNumber: string,
    translations: CNTranslations,
    locale: string,
  ): string | undefined {
    if (!cnNumber) {
      return undefined;
    }

    // Try a zero-padded match first (e.g., 6-digit -> 8-digit with 00)
    if (cnNumber.length < 8) {
      const padded = cnNumber.padEnd(8, '0');
      if (translations[padded]) {
        return translations[padded];
      }
    } else if (cnNumber.length > 8) {
      const truncated = cnNumber.substring(0, 8);
      if (translations[truncated]) {
        return translations[truncated];
      }
    }

    const sortedKeys = this.getSortedKeys(locale, translations);

    if (cnNumber.length < 8) {
      const candidates = sortedKeys.filter(
        (key) => key.length === 8 && key.startsWith(cnNumber),
      );

      if (candidates.length > 0) {
        const bestMatch = candidates
          .sort((a, b) => {
            const aPreferential = a.endsWith('00') ? 0 : 1;
            const bPreferential = b.endsWith('00') ? 0 : 1;

            if (aPreferential !== bPreferential) {
              return aPreferential - bPreferential;
            }

            return a.localeCompare(b);
          })[0];

        return translations[bestMatch];
      }
    }

    // As a final fallback, try reducing the code length (8 -> 6 -> 4)
    for (let length = Math.min(cnNumber.length, 8) - 2; length >= 4; length -= 2) {
      const prefix = cnNumber.substring(0, length);
      if (translations[prefix]) {
        return translations[prefix];
      }
    }

    return undefined;
  }

  /**
   * Get translated CN name with caching
   */
  async getTranslatedName(cnNumber: string, defaultName: string | undefined, locale?: string): Promise<string> {
    if (!cnNumber) {
      return defaultName ?? '';
    }

    const targetLocale = locale || i18n.language;
    const normalizedCode = this.normalizeCode(cnNumber);

    if (!normalizedCode) {
      return defaultName ?? '';
    }

    // Ensure translations are loaded for this locale
    const translations = await this.loadTranslations(targetLocale);

    if (translations[normalizedCode]) {
      return translations[normalizedCode];
    }

    const fallback = this.findFallbackTranslation(normalizedCode, translations, targetLocale);

    if (fallback) {
      this.registerCachedTranslation(targetLocale, normalizedCode, fallback);
      return fallback;
    }

    return defaultName ?? '';
  }

  /**
   * Load translations for a specific locale with caching
   */
  private async loadTranslations(locale: string): Promise<CNTranslations> {
    // Return cached translations if available
    if (this.translations.has(locale)) {
      return this.translations.get(locale)!;
    }

    // Return existing loading promise if already loading
    if (this.loadingPromises.has(locale)) {
      return this.loadingPromises.get(locale)!;
    }

    // Start new loading process
    const loadingPromise = this.fetchTranslations(locale);
    this.loadingPromises.set(locale, loadingPromise);

    try {
      const translations = await loadingPromise;
      this.translations.set(locale, translations);
      this.translationKeys.delete(locale);
      this.loadingPromises.delete(locale);
      return translations;
    } catch (error) {
      this.loadingPromises.delete(locale);
      throw error;
    }
  }

  /**
   * Fetch translations using dynamic imports from src folder
   */
  private async fetchTranslations(locale: string): Promise<CNTranslations> {
    try {
      console.log(`Loading CN translations for locale: ${locale}`);
      let translations: CNTranslations = {};
      
      switch (locale) {
        case 'en':
          translations = (await import('../locales/en/cn-codes.json')).default;
          break;
        case 'cn':
          translations = (await import('../locales/cn/cn-codes.json')).default;
          break;
        case 'tr':
          translations = (await import('../locales/tr/cn-codes.json')).default;
          break;
        default:
          console.warn(`No CN translations found for locale: ${locale}`);
          translations = {};
      }
      
      console.log(`Loaded ${Object.keys(translations).length} CN translations for ${locale}`);
      return translations;
    } catch (error) {
      console.error(`Error loading CN translations for ${locale}:`, error);
      return {};
    }
  }

  /**
   * Preload translations for a locale
   */
  async preloadTranslations(locale: string): Promise<void> {
    await this.loadTranslations(locale);
  }

  /**
   * Get all translations for a locale
   */
  async getAllTranslations(locale: string): Promise<CNTranslations> {
    return this.loadTranslations(locale);
  }

  /**
   * Clear cache for a specific locale or all locales
   */
  clearCache(locale?: string): void {
    if (locale) {
      this.translations.delete(locale);
      this.translationKeys.delete(locale);
      this.loadingPromises.delete(locale);
    } else {
      this.translations.clear();
      this.translationKeys.clear();
      this.loadingPromises.clear();
    }
  }
}

// Export singleton instance
export const cnTranslationService = new CNTranslationService();
